/* eslint-disable no-shadow */
export interface Route {
  path: string;
  roles?: string[];
  routes?: Route[];
  organisation?: boolean;
}

export enum HtmlDocumentType {
  TERMS_OF_USE = 'terms-of-use',
  ABOUT_US = 'about-us',
  PRIVACY_POLICY = 'privacy-policy',
  FAQ = 'faq',
}

export interface ApiDoc {
  id: string;
  groupName: string;
  apiCategory: Category[];
  apiName: string;
  swagger: string;
  iconName: string;
  apiImage: string;
  slug: string;
  tags: string[];
  postmanCollectionId?: string;
  updatedAt: string;
  description: string;
  isPublic?: boolean;
  showApiOverview?: boolean;
  votes: number;
  userLikes: number;
  rating: number;
  numRaters: number;
  complianceLevels: ComplianceLevel[];
}

export interface ComplianceLevel {
  id: string;
  name: string;
  description: string;
}

export interface Organisation {
  id: string;
  displayName: string;
  name: string;
  logo?: string;
  isActive: boolean;
  isPublic: boolean;
  isAuthenticatedOnly: boolean;
  locCountry?: string;
  description?: string;
  subdomain?: string;
  primaryColor: string;
  accentColor: string;
  socialSites: {
    linkedIn?: string;
    google?: string;
    facebook?: string;
    website?: string;
    twitter?: string;
  };
  rating: number;
  numRaters: number;
  complianceLevels: ComplianceLevel[];
  clientId?: string;
  clientSecret?: string;
}

export interface Entity {
  id: string;
  isPublic: boolean;
  isActive: boolean;
  locCountry: string;
  locRegion: {
    totalFintechs: number;
    id: string;
    totalEntities: number;
    name: string;
    totalNeobanks: number;
    totalBanks: number;
  };
  apiPortalLink: string;
  description: string;
  entityType: string;
  subdomain: string;
  shortName: string;
  legalName: string;
  displayName: string;
  logo: string;
  primaryColor: string;
  updatedAt: string;
  accentColor: string;
  showProcess: boolean;
  showTestimonials: boolean;
  showNews: boolean;
  showPartnerships: boolean;
  showShowcase: boolean;
  showSubscribe: boolean;
  contact: string;
  faq: string;
  contactUs: string;
  aboutUs: string;
  termsAndConditions: string;
  privacyPolicy: string;
  landingPageTitleLine1: string;
  landingPageTitleLine2: string;
  landingPageTitleLine3: string;
  landingPageWelcomeHeaderText: string;
  landingPageWelcomeBodyText: string;
  landingPageAppIntroHeaderText: string;
  landingPageAppIntroBodyText: string;
  landingPageApiIntroHeaderText: string;
  landingPageApiIntroBodyText: string;
  landingPageNewsIntroText: string;
  dataSource: string;
  rating: number;
  numRaters: number;
  createdBy: string;
  updatedBy: string;
  showApiOverview?: boolean;
  showHeaderName: boolean;
}

export interface AuthUser {
  firstName: string;
  lastName: string;
  roles: string[];
  organisationId: string;
}

export interface SiteMetadata {
  organisation: Organisation;
}

export interface FeaturedApi {
  apiName: string;
  description: string;
  publisherName: string;
  publisherLogo: string;
  url: string;
}


export interface UserMembership {
  createdAt: string;
  userId: string;
  email: string;
  firstName: string;
  lastName: string;
  lastLoginAt?: string;
  listRoles: string[];
}

export interface OrganisationMembership {
  listCustomFields: [];
  orgId: string;
  orgType: string;
}

export interface Paging {
  pageNumber: number;
  pageSize: number;
  totalRecords: number;
}

export interface BankCustomer {
  customerId: string;
  username: string;
  firstName: string;
  lastName: string;
  status: string;
  bankCode: string;
  createdAt: string;
  updatedAt: string;
}

export interface BankTransaction {
  AccountId: string;
  Amount: {
    Amount: string;
    Currency: string;
  };
  BookingDateTime: string;
  CreditDebitIndicator: string;
  Status: string;
  TransactionId: string;
  TransactionInformation: string;
  TransactionReference: string;
  ValueDateTime: string;
}

export interface BankAccount {
  accountId: string;
  currency: string;
  accountSubType: string;
  sortCodeAccountNumber: string;
  transactions: {
    data: BankTransaction[];
    currentPage: number;
    totalPages: number;
  };
  balance?: AccountBalance;
}

export interface Invitations {
  organisationId: string;
  firstName: string;
  lastName: string;
  emailAddress: string;
}

export interface AppInfo {
  id: string;
  appFeatures: FeatureItem[];
  appImageList: string[];
  appStoreLink: string;
  description: string;
  name: string;
  playStoreLink: string;
  updatedAt: string;
  published: boolean;
  sequence: number;
  ratedScore?: number;
  liked?: boolean;
  rating: number;
  numRaters: number;
  thankMessage: string;
  demoVideo: string;
}

export type AppsInfo = AppInfo[];

export interface Category {
  id: string;
  industry: string;
  entryType: string;
  name: string;
  icon: string;
  description: string;
  order: number;
}

export type Categories = Category[];

export interface AssetSocial {
  id: string;
  ratedAs: string;
  rateType: string;
  ratedAsset: string;
  ratedId: string;
  ratedScore: number;
  comments: string;
}

export type AssetsSocial = AssetSocial[];

export enum ReloadType {
  api = 'API',
  app = 'APP',
  data = 'DATA',
}

export interface Country {
  attributes: {
    active: boolean;
    area: number | string;
    capitalCity: string;
    capitalLatitude: number | string;
    capitalLongtitude: number | string;
    code2: string;
    code3: string;
    currencyInfo: {
      listCurrency: {
        code: string;
        isPrimary: boolean;
        name: string;
        symbol: string;
      }[];
    };
    flagUrlRect: string;
    flagUrlRound: string;
    gdp: number | string;
    idd: string;
    internetHostNumber: number | string;
    internetUserNumber: number | string;
    latitude: number | string;
    longitude: number | string;
    mobilephonesNumber: number | string;
    name: string;
    telephonesNumber: number | string;
    timezone: string;
  };
  id: number | string;
  type: string;
}

export interface UserSocial {
  socialId: string;
  socialNetwork: string;
  firstName: string;
  lastName: string;
  fullName: string;
  email: string;
  avatar: string;
}

export interface Article {
  id: string;
  title: string;
  sourceUrl: string;
  content: string;
  imageUrl: string;
  author: string;
  summary: string;
  publishedAt: string;
  slug: string;
  tags: string[];
  isFeatured: boolean;
  isActive: boolean;
  isPublic: boolean;
  createdAt: string;
  updatedAt: string;
}

export type Articles = Article[];

export interface DocOverview {
  id: string;
  tags: string;
  docString: string;
}

export interface AccountBalance {
  AccountId: string;
  Amount: {
    Amount: string;
    Currency: string;
  };
  CreditDebitIndicator: string;
  DateTime: string;
  Type: string;
}

export enum EntryType {
  API = 'API',
  APP = 'APP',
}

export interface InvitationCode {
  firstName?: string;
  lastName?: string;
  userExisted?: string;
  emailAddress: string;
  organisationId: string;
  iss: string;
  callbackUrl: string;
  exp: number;
  jti: string;
  iat: number;
}

export enum LegalDocumentType {
  TERMS_OF_USE = 'terms-of-use',
  ABOUT_US = 'about-us',
  PRIVACY_POLICY = 'privacy-policy',
  FAQ = 'faq',
}

export interface FeatureItem {
  id: string;
  industry: string;
  featureItem: string;
  icon: string;
  featureDescription: string;
  order: number;
  active: boolean;
  featureGroup: FeatureGroup;
}

export type FeatureItems = FeatureItem[];
export interface FeatureGroup {
  id: string;
  name: string;
  description: string;
  order: number;
  active: boolean;
  type: string;
}

export interface CreateAppResponse {
  appBuildId: string;
  status: string;
}

export interface Wallet {
  walletId: string;
  userId: string;
  walletName: string;
  currentBalance: number;
  availableBalance: number;
  minimumBalance: number;
  bankAccount: {
    bankCode: string;
    accountNumber: string;
    accountHolderName: string;
    productId: string;
    internalProductCategory: string;
    openingDate: string;
  };
  currencyCode: string;
  status: string;
  type: string;
  isDefaultWallet: false;
  expiredAt: string;
}

export interface WalletTransaction {
  txnId: string;
  txnType: string;
  txnCategory: string;
  txnDateTime: string;
  txnCode: string;
  description: string;
  transactionReference: string;
  externalRefNo: string;
  recipientDescription: string;
  creditDebitIndicator: string;
  recipientReference: string;
  destinationAccount: {
    accountName: string;
    accountNumber: string;
  };
  sourceAccount: {
    accountName: string;
    accountNumber: string;
  };
  merchantDetails: {
    merchantCategoryCode: string;
    merchantID: string;
    merchantName: string;
  };
  amount: {
    amount: number;
    currency: string;
  };
  totalFeeAmount: {
    amount: number;
  };
  status: string;
  balanceAfter: number;
  paymentTerminal: {
    name: string;
  };
  paymentNumber: string;
}

export interface Pagination {
  current: number;
  pageSize: number;
  total?: number;
}

export interface Currency {
  name: string;
  code: string;
  symbol: string;
  decimals: number;
  displaySymbol: string;
  displayFormat: string;
  displaySymbolFirst: boolean;
  symbolGraphic?: string;
  currencyType?: string;
  networks?: [];
  isoCode: string;
  isTop?: boolean;
  displaySpace: number;
  logo: string;
}

export enum CurrencyDisplay {
  Symbol = 'symbol',
  Code = 'code',
}
